"use client";

/**
 * VOS Movimento · o provider. Mora UMA vez na raiz de cada app (web, PDV) e
 * faz três coisas:
 *
 * 1. `LazyMotion`: os componentes do sistema usam `m.*`, que nasce leve; o
 *    arraste e o layout (`domMax`) chegam depois da primeira pintura.
 * 2. `MotionConfig`: respeita "reduzir movimento" do sistema operacional (ou a
 *    escolha do VOS), e dá a transição padrão pra quem não pediu nenhuma.
 * 3. Contexto: `useMovimento()` devolve as receitas já na escala e no modo
 *    certos, então nenhum componente calcula tempo.
 */
import { createContext, useContext, useMemo, useSyncExternalStore, type ReactNode } from "react";
import { LazyMotion, MotionConfig, useReducedMotion, type FeatureBundle, type LazyFeatureBundle } from "motion/react";
import { criarMovimento, type Movimento } from "./receitas";
import { CURVA, DUR } from "./tokens";

const carregarRecursos = () => import("./recursos").then((modulo) => modulo.default);

const ContextoDoMovimento = createContext<Movimento | null>(null);

const semAviso = () => () => {};

/**
 * `false` no servidor e no render da hidratação; `true` depois, e desde o
 * primeiro render de quem monta no cliente (navegar pelo app, dado que chegou
 * por fetch). Separa "isto veio pintado do servidor" de "isto está chegando
 * agora". É o mesmo sinal que o provider passa às receitas (`mov.hidratado`).
 */
export function useHidratado(): boolean {
  return useSyncExternalStore(semAviso, () => true, () => false);
}

export type ModoReduzir = "sistema" | "sempre" | "nunca";

export function MovimentoProvider({
  children,
  escala = 1,
  reduzir = "sistema",
  recursos = carregarRecursos,
}: {
  children: ReactNode;
  /**
   * Os recursos do Motion (arraste, layout, animação). Padrão: sob demanda,
   * depois da 1ª pintura (o pacote inicial fica enxuto: é o que o kiosk do PDV
   * usa). O web passa o `domMax` já carregado: no intervalo sem recursos um
   * `m.*` não anima, o que o card na mão e um `animate` que muda no primeiro
   * segundo não podem esperar.
   */
  recursos?: FeatureBundle | LazyFeatureBundle;
  /** Multiplica todo tempo: 1 normal, 2 câmera lenta, 0 sem movimento. */
  escala?: number;
  /** "sistema" segue o sistema operacional; "sempre" e "nunca" forçam. */
  reduzir?: ModoReduzir;
}) {
  const pedidoDoSistema = useReducedMotion();
  /* O servidor não sabe do "reduzir movimento": ele desenha a receita cheia.
     Se o cliente já nascesse reduzido, o `style` inicial da hidratação não
     batia com o do servidor (aviso de hidratação em toda tela). O pedido do
     sistema passa a valer logo DEPOIS de montar; até lá o `MotionConfig`
     (reducedMotion "user") já segura os transforms. */
  const montado = useHidratado();
  const reduzido = reduzir === "sempre" || (reduzir === "sistema" && montado && !!pedidoDoSistema);
  /* `montado` também diz às receitas que a hidratação acabou: antes disso o
     que veio do servidor nasce no lugar, sem `initial` (ver `hidratado`). */
  const valor = useMemo(() => criarMovimento({ escala, reduzido, hidratado: montado }), [escala, reduzido, montado]);
  const modo = reduzir === "sempre" ? "always" : reduzir === "nunca" ? "never" : "user";

  return (
    <LazyMotion features={recursos}>
      <MotionConfig
        reducedMotion={modo}
        transition={{ duration: DUR.md * valor.escala, ease: [...CURVA.entrada] }}
      >
        <ContextoDoMovimento.Provider value={valor}>{children}</ContextoDoMovimento.Provider>
      </MotionConfig>
    </LazyMotion>
  );
}

/**
 * As receitas SÓ quando há um `MovimentoProvider` em volta; fora dele, `null`.
 * É a chave dos primitivos durante o laboratório: dentro do provider eles
 * andam no sistema novo, fora dele seguem como estão até a aprovação.
 */
export function useMovimentoAtivo(): Movimento | null {
  return useContext(ContextoDoMovimento);
}

let avisou = false;

/**
 * As receitas do movimento. Fora do provider ainda funciona (segue o pedido
 * do sistema operacional), mas os `m.*` não animam sem o `LazyMotion`: em dev
 * isso vira aviso no console.
 */
export function useMovimento(): Movimento {
  const doProvider = useContext(ContextoDoMovimento);
  const pedidoDoSistema = useReducedMotion();
  const avulso = useMemo(() => criarMovimento({ reduzido: !!pedidoDoSistema }), [pedidoDoSistema]);
  if (!doProvider && !avisou && process.env.NODE_ENV !== "production") {
    avisou = true;
    console.warn("[movimento] useMovimento() fora do <MovimentoProvider>: os componentes m.* não vão animar.");
  }
  return doProvider ?? avulso;
}
